import { useAuthStore } from './authStore'

const AUTH_COOKIE_NAME = 'fallback-auth'
const FALLBACK_COOKIE_MAX_AGE_MS = 5 * 60 * 1000

export const FALLBACK_COOKIE_AUTH_WHITELIST = [
  '/get-started/identity-verification/veriff-callback',
] as const

export const STORE_FALLBACK_COOKIE_AUTH_WHITELIST = [
  '/get-started/identity-verification',
] as const

type AuthTokenState = {
  token: string
  expiration: number
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  for (const cookie of document.cookie.split(';')) {
    const trimmedCookie = cookie.trim()
    const separatorIndex = trimmedCookie.indexOf('=')
    if (separatorIndex === -1) continue

    const key = trimmedCookie.slice(0, separatorIndex)
    if (key !== name) continue

    return decodeURIComponent(trimmedCookie.slice(separatorIndex + 1))
  }

  return null
}

function setCookie(name: string, value: string, expirationMs: number): void {
  if (typeof document === 'undefined') return

  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${new Date(expirationMs).toUTCString()}; path=/${secure}`
}

function parseAuthCookieValue(value: string): AuthTokenState | null {
  try {
    const parsed = JSON.parse(value) as AuthTokenState
    if (
      typeof parsed.token === 'string' &&
      typeof parsed.expiration === 'number'
    ) {
      return parsed
    }
  } catch {
    return null
  }

  return null
}

export function handleFallbackCookieAuth(): void {
  const authTokenFromCookie = getCookie(AUTH_COOKIE_NAME)
  if (authTokenFromCookie == null || authTokenFromCookie === '') return

  const auth = parseAuthCookieValue(authTokenFromCookie)
  if (auth == null) return

  useAuthStore.getState().setAuthToken(auth)
}

export function storeFallbackCookieAuth(): void {
  const auth = useAuthStore.getState().auth
  if (auth == null) return

  const cookieExpirationMs = Math.min(
    Date.now() + FALLBACK_COOKIE_MAX_AGE_MS,
    auth.expiration
  )

  setCookie(AUTH_COOKIE_NAME, JSON.stringify(auth), cookieExpirationMs)
}

export function isFallbackCookieAuthRoute(pathname: string): boolean {
  return FALLBACK_COOKIE_AUTH_WHITELIST.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  )
}

export function isStoreFallbackCookieAuthRoute(pathname: string): boolean {
  return (STORE_FALLBACK_COOKIE_AUTH_WHITELIST as readonly string[]).includes(
    pathname
  )
}

export function tryFallbackCookieAuthForRoute(pathname: string): boolean {
  if (!isFallbackCookieAuthRoute(pathname)) return false

  handleFallbackCookieAuth()
  return useAuthStore.getState().auth != null
}

export function storeFallbackCookieAuthForRoute(pathname: string): void {
  if (!isStoreFallbackCookieAuthRoute(pathname)) return

  storeFallbackCookieAuth()
}
